import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import StepIndicator from './StepIndicator';
import MoldSelection from './MoldSelection';
import {
  CopyMode,
  Mold,
  OrderDetail,
  MoldOrderFirstLevelType,
  MoldOrderType,
} from '../../types/mold';
import MoldOrderForm from './MoldOrderForm';
import MoldOrderReview, { type CreatedOrderResult } from './MoldOrderReview';
import SuccessState from './SuccessState';
import { useMoldOrderDetail } from '../../hooks/useMoldOrder';
import MoldOrderIcon from '../shared/MoldOrderIcon';
import {
  getIconColorForType,
  transformFromPreviousOrder,
} from '../../models/moldOrder';
import { buildCarriedForm } from '../../models/moldOrder/carryForward';
import { loadFormConfigWithSteps } from '../Questionnaire/formConfigLoader';
import { useAppState } from '../../context/AppStateContext';
import MainContainer from '../layout/MainContainer';

interface CreateMoldOrderProps {
  molds: Mold[];
}

type MoldOrderMapping = Record<string, OrderDetail>;

const CreateMoldOrder: React.FC<CreateMoldOrderProps> = ({ molds }) => {
  const [searchParams] = useSearchParams();
  const { getMoldOrderMainTypeLabel, getHeaderStyleConfig, getFormFields } =
    useAppState();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedMolds, setSelectedMolds] = useState<string[]>([]);
  const [moldOrders, setMoldOrders] = useState<MoldOrderMapping>({});
  const [createdOrders, setCreatedOrders] = useState<Array<{
    moldId: string;
    orderId: string;
    details: OrderDetail;
    mold: Mold;
  }> | null>(null);
  const [partiallyCreatedOrders, setPartiallyCreatedOrders] = useState<CreatedOrderResult[]>([]);
  const [isPreSelected, setIsPreSelected] = useState(false);
  // Count of parent answers that could not be carried (new form version dropped them).
  const [droppedCount, setDroppedCount] = useState(0);
  const { fetchMoldOrderDetailById } = useMoldOrderDetail();

  // Extract URL parameters (primitives, so the pre-fill effect can depend on these
  // directly rather than the whole searchParams object — fewer needless re-runs).
  const selectedParam = searchParams.get('selected');
  const parentOrderIdParam = searchParams.get('parentOrderId');
  const typeParam = searchParams.get('type');
  const subTypeParam = searchParams.get('subType');
  const attentionMode = searchParams.get('attention') === 'true';
  const isSubOrder = searchParams.get('isSubOrder') === 'true';
  // How much of the parent order to carry forward: none | metadata | all.
  const copyModeParam = searchParams.get('copyMode');
  const copyMode: CopyMode =
    copyModeParam === 'all' || copyModeParam === 'metadata'
      ? copyModeParam
      : 'none';
  const moldOrderMainType = typeParam
    ? (typeParam as MoldOrderFirstLevelType)
    : null;
  const parentOrderId = parentOrderIdParam
    ? parseInt(parentOrderIdParam)
    : undefined;

  const headerStyleConfig = getHeaderStyleConfig(moldOrderMainType);

  // The create-form field keys for this type/company come straight from the runtime form
  // schema — a follow-up copies exactly these fields from the parent (as-is). getFormFields
  // is context-derived and stable per type, so recompute only when the type changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const carryKeys = useMemo(
    () =>
      getFormFields(moldOrderMainType ?? 'maintenance').map((field) =>
        String(field.key),
      ),
    [moldOrderMainType],
  );

  // Pre-fill the create flow from URL params. One deterministic writer initialises EVERY
  // selected mold to a blank detail, then (for a follow-up from a rejected order) patches
  // the parent's detail onto its mold via a functional setState MERGE — so it never
  // clobbers siblings and no selected mold is left undefined (which throws at review).
  useEffect(() => {
    if (!selectedParam) return;

    const validSelectedMolds = selectedParam
      .split(',')
      .filter((id) => molds.some((mold) => parseInt(mold.id) === parseInt(id)));
    if (validSelectedMolds.length === 0) return;

    setSelectedMolds(validSelectedMolds);
    setIsPreSelected(true);
    setCurrentStep(2);

    const initialMoldOrders: MoldOrderMapping = {};
    validSelectedMolds.forEach((moldId) => {
      const isAutoFillOrderType = ['mold_acceptance', 'mold_export'].includes(
        moldOrderMainType ?? '',
      );
      initialMoldOrders[moldId] = {
        moldOrderType: subTypeParam
          ? (subTypeParam as MoldOrderType)
          : isAutoFillOrderType
            ? (moldOrderMainType as MoldOrderType)
            : ('' as MoldOrderType),
        note: '',
        form: {},
      };
    });
    setMoldOrders(initialMoldOrders);

    setDroppedCount(0);

    // Follow-up from an existing order. 'metadata' carries only order-CREATION inputs
    // (the questionnaire form + photos are re-filled for the new job); 'all' additionally
    // carries the version-diffed questionnaire answers (and the backend clones photos).
    if (parentOrderIdParam && !isSubOrder && copyMode !== 'none') {
      let cancelled = false;
      const targetMoldId = validSelectedMolds[0];
      (async () => {
        try {
          const previousOrder =
            await fetchMoldOrderDetailById(parentOrderIdParam);
          if (cancelled || !previousOrder) return;

          // Only 'all' needs the parent's questionnaire; version-diff it against the new
          // order's (currently-published) schema and keep still-valid keys. Falls back to
          // a blank form if either schema cannot be resolved.
          let carriedForm: Record<string, any> = {};
          let dropped = 0;
          if (copyMode === 'all') {
            try {
              const group = moldOrderMainType ?? 'maintenance';
              const newOrderPreview = {
                ...previousOrder,
                id: 0,
                orderTemplateId: undefined,
                orderTemplate: undefined,
                orderDetail: {
                  ...previousOrder.orderDetail,
                  formVersion: undefined,
                },
              };
              const [oldConfig, newConfig] = await Promise.all([
                loadFormConfigWithSteps(previousOrder, group),
                loadFormConfigWithSteps(newOrderPreview as never, group),
              ]);
              if (cancelled) return;
              const result = buildCarriedForm(
                previousOrder.orderDetail?.form,
                oldConfig.sections,
                newConfig.sections,
              );
              carriedForm = result.form;
              dropped = result.droppedCount;
            } catch (schemaError) {
              console.error(
                'Could not resolve form schema for copy; starting blank:',
                schemaError,
              );
            }
          }

          const transformedOrder = transformFromPreviousOrder(
            moldOrderMainType,
            previousOrder.orderDetail,
            copyMode,
            carriedForm,
            // Preserve the source's EXACT subtype (from the URL) on the follow-up.
            (subTypeParam as MoldOrderType | null) ?? undefined,
            carryKeys,
          );
          if (cancelled) return;
          setDroppedCount(dropped);
          setMoldOrders((prev) => ({
            ...prev,
            [targetMoldId]: transformedOrder,
          }));
        } catch (error) {
          console.error('Failed to fetch previous mold order:', error);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
  }, [
    selectedParam,
    molds,
    fetchMoldOrderDetailById,
    moldOrderMainType,
    subTypeParam,
    isSubOrder,
    parentOrderIdParam,
    copyMode,
    carryKeys,
  ]);

  const handleNextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handleNextReviewStep = (orders: Record<string, OrderDetail>) => {
    setMoldOrders((previousOrders) => ({ ...previousOrders, ...orders }));
    setCurrentStep((prev) => prev + 1);
  };

  const handlePreviousStep = () => {
    if (!isPreSelected || currentStep > 2) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleOrderSuccess = (
    orders: Array<{
      moldId: string;
      orderId: string;
      details: OrderDetail;
      mold: Mold;
    }>,
  ) => {
    setCreatedOrders(orders);
  };

  const goToStep = (step: number) => {
    const selectionLockedByPartialSuccess = partiallyCreatedOrders.length > 0 && step === 1;
    if (
      step <= currentStep &&
      (!isPreSelected || step > 1) &&
      !selectionLockedByPartialSuccess
    ) {
      setCurrentStep(step);
    }
  };

  if (createdOrders && createdOrders.length > 0) {
    return <SuccessState items={createdOrders} />;
  }

  // Get the filtered molds array based on selected molds
  const getFilteredMolds = () => {
    if (selectedMolds.length === 0) {
      return molds;
    }
    return molds.filter(
      (m) =>
        selectedMolds.includes(m.id.toString()) || selectedMolds.includes(m.id),
    );
  };

  const renderStep = () => {
    const filteredMolds = getFilteredMolds();
    const partiallyCreatedMoldIds = new Set(partiallyCreatedOrders.map((order) => order.moldId));
    const pendingMolds = filteredMolds.filter((mold) => !partiallyCreatedMoldIds.has(mold.id));

    switch (currentStep) {
      case 1:
        return (
          <MoldSelection
            molds={molds}
            onNextStep={handleNextStep}
            selectedMolds={selectedMolds}
            onSelectedMoldsChange={setSelectedMolds}
            moldOrderMainType={moldOrderMainType}
            attentionMode={attentionMode}
          />
        );
      case 2:
        if (filteredMolds.length === 0) {
          // If no molds are selected, go back to step 1
          setCurrentStep(1);
          return null;
        }
        return (
          <MoldOrderForm
            molds={pendingMolds}
            initialOrders={moldOrders}
            onNextStep={handleNextReviewStep}
            onPrevStep={handlePreviousStep}
            isPreselected={isPreSelected || partiallyCreatedOrders.length > 0}
            parentOrderId={parentOrderId}
            moldOrderFirstLevelType={moldOrderMainType ?? 'maintenance'}
          />
        );
      case 3:
        return (
          <MoldOrderReview
            molds={filteredMolds}
            orders={moldOrders}
            isSubOrder={isSubOrder}
            onPrevStep={handlePreviousStep}
            onOrderSuccess={handleOrderSuccess}
            parentOrderId={parentOrderId}
            copyMode={copyMode}
            moldOrderFirstLevelType={moldOrderMainType ?? 'maintenance'}
            previouslyCreatedOrders={partiallyCreatedOrders}
            onPartialSuccess={setPartiallyCreatedOrders}
          />
        );
      default:
        return null;
    }
  };

  const CustomHeader = (
    <div className='relative flex flex-row items-center'>
      <div
        className={`flex items-center justify-center w-6 h-6 sm:w-12 sm:h-12 rounded-xl bg-white/90 backdrop-blur-sm mr-2 sm:mr-4 shadow-md border border-white/50`}
      >
        <MoldOrderIcon
          moldOrderMainType={moldOrderMainType}
          className={`w-3 sm:w-6 h-3 sm:h-6 ${moldOrderMainType ? getIconColorForType(moldOrderMainType) : 'text-gray-600'}`}
        />
      </div>
      <h1
        className={`text-sm sm:text-2xl font-bold text-${headerStyleConfig.colorCode}-800`}
      >
        สร้างใบงาน
        {moldOrderMainType ? getMoldOrderMainTypeLabel(moldOrderMainType) : ''}
      </h1>
    </div>
  );

  return (
    <MainContainer customHeader={CustomHeader}>
      <div className='pt-8 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 min-h-screen px-4'>
        <div className='max-w-7xl mx-auto'>
          <div className='flex justify-between mb-6 sm:mb-8'>
            <StepIndicator
              step={1}
              currentStep={currentStep}
              label='เลือกแม่พิมพ์'
              onClick={() => goToStep(1)}
              disabled={isPreSelected || partiallyCreatedOrders.length > 0}
            />
            <StepIndicator
              step={2}
              currentStep={currentStep}
              label='กรอกข้อมูล'
              onClick={() => goToStep(2)}
            />
            <StepIndicator
              step={3}
              currentStep={currentStep}
              label='ตรวจสอบและยืนยัน'
              onClick={() => goToStep(3)}
            />
          </div>

          {copyMode !== 'none' && isPreSelected && parentOrderId != null ? (
            <div
              data-testid='copy-form-banner'
              className='mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-start gap-2'
            >
              <svg
                className='w-4 h-4 mt-0.5 flex-shrink-0'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                  clipRule='evenodd'
                />
              </svg>
              <span>
                สร้างต่อจากใบงาน #{parentOrderId} ·{' '}
                {copyMode === 'all'
                  ? 'คัดลอกข้อมูลเดิมและรูปภาพมาแล้ว'
                  : 'คัดลอกเฉพาะข้อมูลสำหรับสร้างใบงาน — ฟอร์มและรูปภาพต้องกรอกใหม่'}
                {copyMode === 'all' && droppedCount > 0 ? (
                  <>
                    {' '}
                    · ข้อมูล {droppedCount}{' '}
                    ช่องนำมาไม่ได้เนื่องจากฟอร์มเวอร์ชันใหม่ กรุณากรอกใหม่
                  </>
                ) : null}
              </span>
            </div>
          ) : null}

          {renderStep()}
        </div>
      </div>
    </MainContainer>
  );
};

export default CreateMoldOrder;
